import CardHeader from 'components/CardHeader';
import Icon from 'components/Icon';
import Textarea from 'components/Inputs/Textarea';
import SelectPopupSearchForm from 'components/SearchForm/SelectPopupSearchForm';
import SimplePopup from 'components/SimplePopup';
import Typography from 'components/Typography';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { AiModuleModelType } from 'models/AiModuleModel';
import { HypothesisModelType } from 'models/HypothesisModel';
import { ProjectAiModulesStoreType } from 'models/ProjectAiModulesStore';
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import RainbowCard from 'components/RainbowCard';

type Step = 'confirm' | 'hypotheses' | 'details';

interface AiModulePopupProps {
  aiModule: AiModuleModelType;
  hypotheses: HypothesisModelType[];
  preselectedHypothesisIds?: number[];
  organizationId: number;
  projectId: number;
  projectAiModulesStore: ProjectAiModulesStoreType;
  isOverride?: boolean;
  onClose: () => void;
  onHypothesisToggle?: (hypothesisId: number, checked: boolean) => void;
}

const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  createPortal(children, document.body);

const AiModulePopup: React.FC<AiModulePopupProps> = observer(
  ({
    aiModule,
    hypotheses,
    preselectedHypothesisIds,
    organizationId,
    projectId,
    projectAiModulesStore,
    isOverride,
    onClose,
    onHypothesisToggle
  }) => {
    const intl = useIntl();
    const [step, setStep] = useState<Step>(
      isOverride ? 'confirm' : 'hypotheses'
    );
    const [selectedHypothesisIds, setSelectedHypothesisIds] = useState<
      number[]
    >(preselectedHypothesisIds || []);
    const [additionalInput, setAdditionalInput] = useState('');
    const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const sortedHypotheses = useMemo(() => {
      const preselected = new Set(preselectedHypothesisIds);
      return [...hypotheses].sort((a, b) => {
        const aSelected = preselected.has(a.id) ? 0 : 1;
        const bSelected = preselected.has(b.id) ? 0 : 1;
        return aSelected - bSelected;
      });
    }, [hypotheses, preselectedHypothesisIds]);

    const filteredHypotheses = useMemo(() => {
      if (!searchQuery) return sortedHypotheses;
      const query = searchQuery.toLowerCase();
      return sortedHypotheses.filter((h) =>
        (h.headline || '').toLowerCase().includes(query)
      );
    }, [sortedHypotheses, searchQuery]);

    const handleHypothesisToggle = useCallback(
      (hypothesisId: number, checked: boolean) => {
        setSelectedHypothesisIds((prev) =>
          checked
            ? [...prev, hypothesisId]
            : prev.filter((id) => id !== hypothesisId)
        );
        onHypothesisToggle?.(hypothesisId, checked);
      },
      [onHypothesisToggle]
    );

    const handleTagSelect = useCallback((tagId: number) => {
      setSelectedTagId((prev) => (prev === tagId ? null : tagId));
    }, []);

    const handleSubmit = useCallback(async () => {
      const result = await projectAiModulesStore.generateAiModule(
        organizationId,
        projectId,
        aiModule.id,
        selectedHypothesisIds,
        additionalInput || undefined,
        selectedTagId ? selectedTagId : undefined
      );

      // The API now returns immediately — the job runs in the background.
      // Close the popup right away; status updates arrive via ActionCable.
      if (result) {
        onClose();
      }
    }, [
      aiModule.id,
      additionalInput,
      onClose,
      organizationId,
      projectAiModulesStore,
      projectId,
      selectedHypothesisIds,
      selectedTagId
    ]);

    const stepConfig = {
      confirm: {
        submitTextId: 'ai_module_popup.override_submit',
        onSubmit: () => setStep('hypotheses' as Step)
      },
      hypotheses: {
        submitTextId: 'Next',
        onSubmit: () => setStep('details' as Step),
        maxHeight: true
      },
      details: {
        submitTextId: 'ai_module_popup.submit',
        onSubmit: handleSubmit
      }
    };

    const { submitTextId, onSubmit, ...popupFlags } = stepConfig[step];

    return (
      <Portal>
        <SimplePopup
          headline={
            <span className="ai-module-popup__headline">
              {aiModule.icon_url ? (
                <img
                  src={aiModule.icon_url}
                  alt={aiModule.name || ''}
                  className="ai-module-popup__headline-icon"
                />
              ) : (
                <Icon name="ai" />
              )}
              {aiModule.name}
            </span>
          }
          onAbort={onClose}
          submitTextId={submitTextId}
          onSubmit={onSubmit}
          front
          {...popupFlags}
        >
          {step === 'confirm' && (
            <p>
              <FormattedMessage id="ai_module_popup.override_text" />
            </p>
          )}

          {step === 'hypotheses' && (
            <div className="ai-module-popup__content">
              <Typography size="small">
                <FormattedMessage id="ai_module_popup.hypothesis_subheadline" />
              </Typography>
              {hypotheses.length === 0 && (
                <Typography>
                  <FormattedMessage id="No hypotheses available" />
                </Typography>
              )}
              {hypotheses.length > 0 && (
                <div className="ai-module-popup__search">
                  <SelectPopupSearchForm
                    placeholder={intl.formatMessage({
                      id: 'Search hypotheses'
                    })}
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setSearchQuery(e.target.value)
                    }
                  />
                </div>
              )}
              <div className="ai-module-popup__hypotheses-container">
                <div className="ai-module-popup__hypotheses-grid">
                  {filteredHypotheses.map((hypothesis) => (
                    <RainbowCard
                      key={hypothesis.id}
                      shrink
                      checked={selectedHypothesisIds.includes(hypothesis.id)}
                      onCheckboxChange={(checked) =>
                        handleHypothesisToggle(hypothesis.id, checked)
                      }
                    >
                      <figure className="ai-module-popup__hypotheses-logo">
                        <img
                          className="ai-module-popup__hypotheses-logo__image"
                          src={hypothesis.attachment?.resource_urls?.small}
                          alt="Test"
                        />
                      </figure>
                      <CardHeader
                        single
                        title={hypothesis.headline}
                      />
                    </RainbowCard>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 'details' && (
            <div className="l-item-stack-regular ai-module-popup__details">
              <Textarea
                name="additional_input"
                label={
                  <span className="ai-module-popup__label">
                    <FormattedMessage id="ai_module_popup.additional_input_label" />
                  </span>
                }
                lines={4}
                normalFont
                value={additionalInput}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAdditionalInput(e.target.value)
                }
                placeholder={intl.formatMessage({
                  id: 'ai_module_popup.additional_input_placeholder'
                })}
              />

              {aiModule.module_tags.length > 0 && (
                <div>
                  <label className="ai-module-popup__label">
                    <FormattedMessage id="ai_module_popup.platform_label" />
                  </label>
                  <div className="ai-module-popup__tags">
                    {aiModule.module_tags.map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        className={classNames('ai-module-popup__tag', {
                          'ai-module-popup__tag--selected':
                            selectedTagId === tag.id
                        })}
                        onClick={() => handleTagSelect(tag.id)}
                      >
                        {tag.name || ''}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </SimplePopup>
      </Portal>
    );
  }
);

export default AiModulePopup;
